import React from 'react';
import AmpTable, {defaultTrProps} from '../Table';
import PropTypes from 'prop-types';
import TabBar from '../../molecules/TabBar';
import {equals, propEq, not, identity, descend} from 'ramda';
import {Bold} from '../../atoms/Text';
import NotLoggedInOverlay from '../../atoms/NotLoggedInOverlay';
import {checkLogin} from '../../auth';
import {hasDowntime, hasBypass, hasCriticalAlarmOrBypass} from '../../utils/systems';
import AlarmCode from '../AlarmCode';
import Loading from '../../molecules/Loading';

const inOperationsFilter = propEq('status', 'In Operations');

const alarmCodes = ['YELLOW', 'ORANGE', 'RED', 'BLACK'];
const categoryOrder = ({alarmCategory}) => alarmCodes.indexOf(alarmCategory);

const AlarmCodeCell = ({value}) =>(<AlarmCode alarmCategory={value.alarmCategory} hasBypass={value.hasBypass}/>);

AlarmCodeCell.propTypes = {
  value: PropTypes.object,
};

const columns = [
  {
    Header: 'Type',
    accessor: 'id',
    width: 80,
  },
  {
    Header: 'Alarm',
    id: 'alarm',
    accessor: system => ({
      alarmCategory: system.alarmCategory,
      hasBypass: hasBypass(system),
    }),
    Cell: AlarmCodeCell,
    width: 80,
    sortMethod: descend(categoryOrder),
  },
  {
    Header: 'Project',
    accessor: 'project.code',
    getProps: () => ({
      style: {
        fontWeight: 'bold',
      },
    }),
  },
  {
    Header: 'Status',
    accessor: 'status',
  },
  {
    Header: 'Downtime',
    accessor: 'downtime',
    Cell: ({value}) => (value > 0 ? `Level ${value}` : 'None'),
  },
  {
    Header: 'Project Owner',
    accessor: 'project.owner.name',
  },
  {
    Header: 'Vessel',
    accessor: 'project.vessel',
  },
];

const tabs = [
  {
    title: 'In Operations',
    tabFilter: inOperationsFilter
  },
  {
    title: 'Downtime',
    tabFilter: hasDowntime
  },
  {
    title: 'Critical Alarms',
    tabFilter: hasCriticalAlarmOrBypass
  },
  {
    title: 'All',
    tabFilter: identity
  }
];

class SystemsList extends React.Component {
  state= {
    selectedTab: 0,
    tabsWithNumbers: tabs,
  }

  componentDidMount() {
    this.countLengths();
  }

  componentDidUpdate(prevProps) {
    not(equals(prevProps.systems, this.props.systems)) && this.countLengths();
  }

  countLengths = () => {
    const {systems} = this.props;
    const tabsWithNumbers = tabs.map(tab => {
      const filteredSystems = systems.filter(tab.tabFilter);
      return {
        ...tab,
        title: `${tab.title} (${filteredSystems.length})`
      };
    });
    this.setState({tabsWithNumbers});
  }

  applyFilter = () => {
    const {systems} = this.props;
    const {selectedTab} = this.state;
    return systems ? systems.filter(tabs[selectedTab].tabFilter) : [];
  }

  onTabChange = (_, i) => this.setState({selectedTab: i});

  render() {
    const {onSelect, selected, loading} = this.props;
    const {selectedTab, tabsWithNumbers} = this.state;
    const filteredSystems = this.applyFilter();
    const isLoggedIn = checkLogin();
    return !isLoggedIn ? <NotLoggedInOverlay /> : (
      <>
        <Bold style={{fontSize: '32px', margin: '60px 40px 20px', textTransform: 'uppercase'}}>Systems</Bold>
        <TabBar
          tabs={tabsWithNumbers}
          style={{margin: '40px 40px 40px'}}
          onSelect={this.onTabChange}
          selected={selectedTab}
        />
        {loading ? <Loading message="Fetching system data..."/> : (
          <AmpTable
            data={filteredSystems}
            style={{
              flex: 1,
              padding: '0 40px 80px 40px',
              height: 'calc(100% - 10px)',
              overflowY: 'auto',
            }}
            columns={columns}
            getTrProps={(_, {original: system}) => {
              return {
                ...defaultTrProps,

                selected: selected && system.id === selected.id,
                onClick: () => onSelect && onSelect(system),
              };
            }}
          />
        )}
      </>
    );
  }
}

SystemsList.propTypes = {
  systems: PropTypes.array.isRequired,
  onSelect: PropTypes.func,
  selected: PropTypes.object,
  loading: PropTypes.bool,
};

export default SystemsList;
